import React, { useRef, useState, useEffect, useCallback, useLayoutEffect } from 'react';
import { VariableSizeList } from 'react-window';
import {
    ActionButtons, DownloadTypeSelection, VideoActions,
    VideoActionsContent, VideoActionsContentTop, VideoLists
} from '../../../../style/VideoStyle';
import Button from '../../../../components/button';
import { convertIsoToYyyyMmDd } from '../helpers';
import { ReactComponent as PlayVideo } from "../../../../images/ic_play-button.svg";
import closeIcon from "../../../../images/ic_close.svg";
import { useLexicon } from '../../../../hooks/useLexicon';
import styled from 'styled-components';
import { formatMatchScore } from '../../../../helpers/matchScore';

// Row heights for the virtualized events list. The list can hold thousands of
// markers; rendering every row (and re-rendering them all on each streamed
// batch) was the main-thread stall, so only the visible rows are mounted.
// Titles are single-line (nowrap + ellipsis) so heights are constant per type.
const EVENT_ROW_HEIGHT = 61;   // 18px*2 padding + 24px line + 1px border
const BRAKER_ROW_HEIGHT = 42;  // 8px*2 padding + 24px line + 1px border
const LOADING_ROW_HEIGHT = 44;

const Checkbox = styled.input`
    position: absolute;
    right: 20px;
    top: 5vh;

    &[type="checkbox"] {
        appearance: none;
        background-color: transparent;
        margin: 0;
        font: inherit;
        width: 16px;
        height: 16px;
        border: 1px solid ${props => props.theme.green};
        border-radius: 2px;
        transform: translateY(-0.075em);
        display: grid;
        place-content: center;
        cursor: pointer;
    }

    &[type="checkbox"]::before {
        content: "";
        width: 8px;
        height: 0.6em;
        clip-path: polygon(14% 44%, 0 65%, 50% 100%, 100% 21%, 80% 0, 43% 62%);
        transform: scale(0);
        transition: transform 0.15s ease-in-out;
        background-color: ${props => props.theme.green};
    }

    &[type="checkbox"]:checked::before {
        transform: scale(1);
    }
`;

// Shown at the bottom of the events list while later marker batches are still
// streaming in, so a user who scrolls to the end sees that more is loading.
const LoadingMoreRow = styled.div`
    box-sizing: border-box;
    padding: 12px 16px;
    text-align: center;
    font-size: 14px;
    opacity: 0.7;
`;

const RestrictedClipsNotice = styled.div`
    margin: 10px 16px 0;
    padding: 10px 12px;
    border-left: 3px solid ${(props) => props.theme.green};
    border-radius: 4px;
    background: rgba(188, 240, 28, 0.1);
    color: ${(props) => props.theme.white};
    font-size: 13px;
    line-height: 1.4;
`;

const CheckBoxPanel = ({ item, checkedItemsMap, toggleAllEventsOnMatch, maxWidthTitle, onShowAccessDenied, style }) => {
    const isChecked = checkedItemsMap[item.id] || false;
    const matchTitle = `${convertIsoToYyyyMmDd(item.match?.date)} ${item.match.home_team.name} ${formatMatchScore(item.match)} ${item.match.away_team.name}`;

    const handleChange = (checked) => {
        // Check if any markers are blocked when trying to select
        if (checked) {
            const hasBlockedMarkers = item.markers?.some(marker => marker.video && marker.video_blocked === true);
            if (hasBlockedMarkers) {
                onShowAccessDenied?.();
            }
        }
        toggleAllEventsOnMatch(checked, item.markers);
    };

    return (
        <VideoLists
            style={style}
            className='video-braker'
            title={matchTitle}
        >
            <>
                <div className="videoList">
                    <h3 style={{ maxWidth: maxWidthTitle }}>
                        {matchTitle}
                    </h3>
                </div>
                <input
                    type="checkbox"
                    checked={isChecked}
                    onChange={(e) => handleChange(e.target.checked)}
                />
            </>
        </VideoLists>
    );
};

export const EventsPanel = (props) => {
    const {
        stacked,
        countEpisodes,
        countMathes,
        countSequences,
        videoCuts,
        handlePlayVideo,
        toggleAllEventsOnMatch,
        toggleAllEvents,
        toggleCheckCut,
        selectedEventsCount,
        onOpenAddPlaylistModal,
        currentVideoIndex,
        loadingDownloadCuts,
        handleDownloadSelectedCuts,
        handleDownloadSelectedCutsInOneClip,
        loadingMarkers,
        isLoadingMore,
        onShowAccessDenied,
        restrictedClipsMessage,
    } = props;

    const l = useLexicon();
    const parent = useRef(null);
    // Virtualized-list plumbing: measure the scroll area so react-window gets a
    // concrete pixel height, and keep a ref to reset its size cache when the list
    // changes or to keep the active row in view.
    const listContainerRef = useRef(null);
    const listRef = useRef(null);
    const [listHeight, setListHeight] = useState(0);

    const [openDownloadTypeSelection, setOpenDownloadTypeSelection] = useState(false);

    // Flatten all selectable items (including braker markers)
    const [checkedItemsMap, setCheckedItemsMap] = useState({});

    useEffect(() => {
        const map = {};
        videoCuts.forEach(item => {
            if (item.braker && item.markers) {
                item.markers.forEach(marker => {
                    map[marker.id] = marker.checked || false;
                });
            } else {
                map[item.id] = item.checked || false;
            }
        });
        setCheckedItemsMap(map);
    }, [videoCuts]);

    const handleToggleAll = (checked) => {
        // Check if any events are blocked when trying to select all
        let hasBlockedEvents = false;
        if (checked) {
            hasBlockedEvents = videoCuts.some(item => {
                if (item.braker && item.markers) {
                    return item.markers.some(marker => marker.video && marker.video_blocked === true);
                }
                return item.video && item.video_blocked === true;
            });
            if (hasBlockedEvents) {
                onShowAccessDenied?.();
            }
        }

        // Only update checkboxes for non-blocked events
        const updatedMap = { ...checkedItemsMap };
        if (checked && hasBlockedEvents) {
            // Only update non-blocked items
            videoCuts.forEach(item => {
                if (item.braker && item.markers) {
                    item.markers.forEach(marker => {
                        if (marker.video && !marker.video_blocked) {
                            updatedMap[marker.id] = checked;
                        }
                    });
                } else {
                    if (item.video && !item.video_blocked) {
                        updatedMap[item.id] = checked;
                    }
                }
            });
        } else {
            // Update all items if unchecking or no blocked events
            Object.keys(updatedMap).forEach(key => updatedMap[key] = checked);
        }
        setCheckedItemsMap(updatedMap);

        videoCuts.forEach(item => {
            if (item.braker && item.markers) {
                toggleAllEventsOnMatch?.(checked, item.markers);
            } else {
                toggleCheckCut?.(checked, item);
            }
        });

        toggleAllEvents?.(checked);
    };

    const handleToggleItem = (id, checked) => {
        const brakerItem = videoCuts.find(item => item.id === id || item.markers?.some(m => m.id === id));
        if (brakerItem?.braker) {
            const marker = brakerItem.markers.find(m => m.id === id);
            // Check if blocked before updating state
            if (checked && marker?.video_blocked === true) {
                onShowAccessDenied?.(marker?.video_premium_allowed);
                return;
            }
            setCheckedItemsMap(prev => ({ ...prev, [id]: checked }));
            toggleAllEventsOnMatch?.(checked, [marker]);
        } else {
            const item = videoCuts.find(vc => vc.id === id);
            // Check if blocked before updating state
            if (checked && item?.video_blocked === true) {
                onShowAccessDenied?.(item?.video_premium_allowed);
                return;
            }
            setCheckedItemsMap(prev => ({ ...prev, [id]: checked }));
            toggleCheckCut?.(checked, item);
        }
    };

    const handlePressDownloadOneClip = () => {
        handleDownloadSelectedCutsInOneClip?.();
        setOpenDownloadTypeSelection(false);
    };

    const handlePressDownloadZip = () => {
        handleDownloadSelectedCuts?.();
        setOpenDownloadTypeSelection(false);
    };

    // main checkbox state
    const allChecked = Object.values(checkedItemsMap).every(Boolean);
    const someChecked = Object.values(checkedItemsMap).some(Boolean);

    // Track the scroll-area height (50vh) so the virtualized list can size itself.
    useLayoutEffect(() => {
        const el = listContainerRef.current;
        if (!el) return;
        const update = () => setListHeight(el.clientHeight);
        update();
        if (typeof ResizeObserver === 'undefined') return;
        const ro = new ResizeObserver(update);
        ro.observe(el);
        return () => ro.disconnect();
    }, []);

    // A trailing sentinel row shows the "loading more" indicator while later
    // marker batches stream in.
    const itemCount = (videoCuts?.length || 0) + (isLoadingMore ? 1 : 0);

    const getItemSize = useCallback((index) => {
        if (index >= (videoCuts?.length || 0)) return LOADING_ROW_HEIGHT;
        return videoCuts[index]?.braker ? BRAKER_ROW_HEIGHT : EVENT_ROW_HEIGHT;
    }, [videoCuts]);

    // Row types (braker vs event) and the list length change as batches arrive,
    // so drop react-window's cached offsets when the data changes.
    useEffect(() => {
        listRef.current?.resetAfterIndex(0);
    }, [videoCuts, isLoadingMore]);

    // Keep the active clip visible when navigating with prev/next.
    useEffect(() => {
        if (listRef.current && currentVideoIndex >= 0) {
            listRef.current.scrollToItem(currentVideoIndex, 'smart');
        }
    }, [currentVideoIndex]);

    const maxWidthTitle = parent.current?.offsetWidth - 66 || 255;

    const Row = useCallback(({ index, style }) => {
        if (index >= (videoCuts?.length || 0)) {
            return <LoadingMoreRow style={style}>{l(312)}</LoadingMoreRow>;
        }
        const item = videoCuts[index];
        if (item?.braker) {
            return (
                <CheckBoxPanel
                    style={style}
                    item={item}
                    maxWidthTitle={maxWidthTitle}
                    checkedItemsMap={checkedItemsMap}
                    toggleAllEventsOnMatch={toggleAllEventsOnMatch}
                    onShowAccessDenied={onShowAccessDenied}
                />
            );
        }

        return (
            <VideoLists
                style={style}
                className={currentVideoIndex === index ? 'active' : ''}
                title={`${!item?.video ? l(316) : item?.creator ? `${item.creator.display_name}` : ''
                }${item.creator_team?.name ? ` - ${item.creator_team?.name} ` : ''}, ${item.action
                }`}
                onClick={() => handlePlayVideo(item?.video, +(item?.second || 0), item, index)}
            >
                <>
                    <div
                        className="videoList"
                        style={{ opacity: item?.video ? 1 : 0.78, pointerEvents: item?.video ? 'auto' : 'none' }}
                    >
                        <PlayVideo />
                        <h3 style={{
                            maxWidth: maxWidthTitle,
                            color: item?.video_blocked === true ? '#999' : 'inherit',
                            opacity: item?.video_blocked === true ? 0.6 : 1
                        }}>
                            {index + 1}. {item?.title}
                        </h3>
                    </div>
                    <input
                        type="checkbox"
                        checked={checkedItemsMap[item.id] || false}
                        disabled={!item.video}
                        onClick={(e) => e.stopPropagation()}
                        onChange={(e) => handleToggleItem(item.id, e.target.checked)}
                    />
                </>
            </VideoLists>
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [videoCuts, checkedItemsMap, currentVideoIndex, maxWidthTitle, l, handlePlayVideo, toggleAllEventsOnMatch, onShowAccessDenied]);

    return (
        <VideoActions ref={parent} data-tour="video-events" $stacked={stacked} $focus={stacked}>
            <VideoActionsContent>
                <h3 className='video-actions-title'>
                    {loadingMarkers ? l(312) : (
                        <>
                            <span>{countEpisodes}</span> {l(9)} • {countSequences > 0 && (<><span>{countSequences}</span> Sequences • </>)}<span>{countMathes}</span> {l(7)}
                        </>
                    )}
                </h3>

                {toggleAllEvents && (
                    <Checkbox
                        type="checkbox"
                        checked={allChecked}
                        ref={el => {
                            if (el) el.indeterminate = someChecked && !allChecked;
                        }}
                        onClick={(e) => e.stopPropagation()}
                        onChange={(e) => handleToggleAll(e.target.checked)}
                    />
                )}

                <VideoActionsContentTop ref={listContainerRef} style={{ overflow: 'hidden' }}>
                    {listHeight > 0 && itemCount > 0 && (
                        <VariableSizeList
                            ref={listRef}
                            height={listHeight}
                            width="100%"
                            itemCount={itemCount}
                            itemSize={getItemSize}
                            overscanCount={6}
                        >
                            {Row}
                        </VariableSizeList>
                    )}
                </VideoActionsContentTop>

                {restrictedClipsMessage && (
                    <RestrictedClipsNotice role="status">
                        {restrictedClipsMessage}
                    </RestrictedClipsNotice>
                )}

                <ActionButtons>
                    <Button
                        disabled={selectedEventsCount === 0}
                        title={selectedEventsCount ? `${l(426)} ${selectedEventsCount} ${l(9)}` : l(426)}
                        style={{ whiteSpace: 'nowrap' }}
                        onClick={() => onOpenAddPlaylistModal(true)}
                    />
                    <Button
                        disabled={videoCuts?.length === 0 || loadingDownloadCuts}
                        title={loadingDownloadCuts ? l(312) : selectedEventsCount ? `${l(116)} ${selectedEventsCount} ${l(9)}` : l(427)}
                        color={"secondary"}
                        style={{ whiteSpace: 'nowrap' }}
                        onClick={() => setOpenDownloadTypeSelection(true)}
                    />
                    {openDownloadTypeSelection && (
                        <DownloadTypeSelection>
                            <Button
                                disabled={loadingDownloadCuts}
                                title={l(1086)}
                                color={"secondary"}
                                onClick={handlePressDownloadOneClip}
                            />
                            <Button
                                disabled={loadingDownloadCuts}
                                title={l(1087)}
                                color={"secondary"}
                                onClick={handlePressDownloadZip}
                            />
                            <img src={closeIcon} alt="close" onClick={() => setOpenDownloadTypeSelection(false)} />
                        </DownloadTypeSelection>
                    )}
                </ActionButtons>
            </VideoActionsContent>
        </VideoActions>
    );
};
